import {
    DateWithSession,
    RenderContext,
    type Session,
} from '@eventbrite/checkout-fairy-api';
import { track } from '@eventbrite/datalayer-library';
import {
    formatDate,
    getFormattedDateTime,
    Locales,
} from '@eventbrite/datetime-fns';
import {
    Button,
    STYLE_FILL,
    STYLE_REBRANDING_FILL,
} from '@eventbrite/eds-button';
import { DatePicker } from '@eventbrite/eds-date-picker-accessible';
import { Divider } from '@eventbrite/eds-divider';
import { ListPicker } from '@eventbrite/eds-list-picker';
import { LoadingOverlay } from '@eventbrite/eds-loading-overlay';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { DateReminder } from '../../components/DateReminder';
import LanguageAndBrand from '../../components/languageAndBrand';
import { HeapEventName } from '../../constants/heap/events';
import { getEventDates } from '../../selectors/event/getEventDates';
import ConnectedPageTemplate from '../template/ConnectedPageTemplate';
import { SeriesEventCardListContainer } from './components/SeriesEventCardListContainer';
import { SeriesEventDatePickerContainer } from './components/SeriesEventDatePickerContainer';
import { SeriesNotification } from './components/SeriesNotification';
import { getFirstAvailableDate } from './utils/getFirstAvailableDate';
import { getFirstAvailableSession } from './utils/getFirstAvailableSession';
import { getFirstDayWithAvailableSessions } from './utils/getFirstDayWithAvailableSessions';
import { getListItemsFromSessions } from './utils/getListItemsFromSessions';
import { getLocalDate, updateMonthOnlineLocalDate } from './utils/getLocalDate';
import getNextAvailableSession from './utils/getNextAvailableSession';
import { getSoldOutDates } from './utils/getSoldOutDates';
import { isDateDisabled } from './utils/isDateDisabled';
import { isEventSoldOut as isEventSoldOutFn } from './utils/isEventSoldOut';

import {
    DEFAULT_STYLES,
    getLoadingOverlayStyle,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';

import { ConnectedInfiniteCalendarSeries } from '../../components/InfiniteCalendarSeries/ConnectedInfiniteCalendarSeries';
import { isInfiniteCalendarExperimentEnabled } from '../../utils/isInfiniteCalendarSeriesEnabled';
import { PageTemplate, TemplateProps } from '../template';
import { INFINITE_CALENDAR_SERIES_EXPERIMENT_LOADING_TIMEOUT } from './constants';
import defaultStyles from './seriesPage.module.scss';
import rebrandStyles from './seriesPage.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export const SOLD_OUT_SESSION_MESSAGE = gettext(
    'The selected session is sold out, please choose another session',
).toString();

export const SOLD_OUT_EVENT_MESSAGE = gettext(
    'All dates and time slots are sold out',
).toString();

export interface SeriesPageProps {
    // user's timezone computed from cloudfront request headers or TLD cookies
    currentTimezone: string;
    endDate: { utc: string; timezone: string };
    eventDates: ReturnType<typeof getEventDates>;
    eventFromStartDateToEndDate: string;
    eventTimezone: string;
    isAppRebrandActive: boolean;
    isEventOnline: boolean;
    isLoading: boolean;
    locale: string;
    onContinue: (eventId: string) => void;
    onMonthChanged: (date: Date, timezone: string) => void;
    onDateChanged: (date: Date) => void;
    onPageFullyLoaded: () => void;
    onPageMounted: () => void;
    onPageMountedLegacy: () => void;
    onSessionSelectedChanged: (selected: {
        date: DateWithSession;
        session: Session;
    }) => void;
    selectedDateWithSession?: {
        date: DateWithSession;
        session: Session;
    };
    startDate: { utc: string; timezone: string };
    shouldShowCalendarSelection: boolean;
}

export const SeriesPage: React.FC<SeriesPageProps> = ({
    currentTimezone,
    endDate,
    eventDates,
    eventFromStartDateToEndDate,
    eventTimezone,
    isAppRebrandActive,
    isEventOnline,
    isLoading,
    locale,
    onContinue,
    onDateChanged,
    onMonthChanged,
    onPageFullyLoaded,
    onPageMounted,
    onSessionSelectedChanged,
    selectedDateWithSession,
    startDate,
    shouldShowCalendarSelection,
    children,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const [
        isInfiniteCalendarSeriesExperimentLoading,
        setIsInfiniteCalendarSeriesExperimentLoading,
    ] = useState<boolean>(true);

    const extraModalBodyProps = { shouldShowFooter: true };
    const extraPaneProps = { isSeriesParentPage: true };
    const firstMonthWithSessions = Object.keys(eventDates)[0];

    const [timezone, setTimeZone] = useState<string>(
        isEventOnline ? currentTimezone || '' : eventTimezone,
    );

    const previouslySelectedMonth = selectedDateWithSession?.session
        .startDatetime
        ? getFormattedDateTime(
              selectedDateWithSession?.session.startDatetime,
              false,
              timezone,
              undefined,
              'yyyy_MM',
          )
        : null;

    const calendarTimezone = isEventOnline ? timezone : startDate.timezone;

    const firstSessionAvailable = getNextAvailableSession(
        eventDates[firstMonthWithSessions],
    );

    const fromMonth = firstSessionAvailable?.startDatetime
        ? utcToZonedTime(firstSessionAvailable?.startDatetime, calendarTimezone)
        : undefined;

    const [showCalendar, setShowCalendar] = useState(
        isEventOnline ? (currentTimezone ? true : false) : true,
    );

    const [isEventSoldOut] = useState<boolean>(
        isEventSoldOutFn(endDate.utc, Object.values(eventDates).flat(1)),
    );

    const [isSelectedSessionSoldOut, setIsSelectedSessionSoldOut] =
        useState<boolean>(
            selectedDateWithSession?.session?.availability === 'soldOut',
        );

    const [selectedMonth, setSelectedMonth] = useState<string>(
        previouslySelectedMonth || firstMonthWithSessions,
    );

    const [month, setMonth] = useState<RenderContext['eventDates']>(
        previouslySelectedMonth && eventDates[previouslySelectedMonth]
            ? eventDates[previouslySelectedMonth]
            : eventDates[firstMonthWithSessions],
    );

    const [date, setDate] = useState<DateWithSession | undefined>(
        selectedDateWithSession?.date || getFirstAvailableDate(month),
    );

    const defaultMonth = selectedDateWithSession?.date
        ? new Date(selectedDateWithSession.date.date)
        : undefined;

    const [session, setSession] = useState<Session | null | undefined>(
        selectedDateWithSession?.session ||
            getNextAvailableSession(eventDates[firstMonthWithSessions]),
    );

    const [initialState] = useState({ onPageMounted });

    const [onlineEventInitialMonth, setOnlineEventInitialMonth] =
        useState<RenderContext['eventDates']>();

    useEffect(() => {
        onPageFullyLoaded();
        if (!shouldShowCalendarSelection) {
            return;
        }

        if (isEventOnline) {
            track({
                eventName: HeapEventName.onlineTimedEntryEventCurrentTimezone,
                eventData: {
                    currentTimezone,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            !shouldShowCalendarSelection ||
            !isEventOnline ||
            onlineEventInitialMonth ||
            showCalendar
        ) {
            return;
        }

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (isEventOnline) {
            setTimeZone(tz);
        }

        const onlineEventMonth = updateMonthOnlineLocalDate(month, tz);
        setTimeout(() => {
            const date =
                selectedDateWithSession?.date ??
                getFirstDayWithAvailableSessions(onlineEventMonth);
            if (date) {
                setDate(date);
            }
            setMonth(onlineEventMonth);
            setShowCalendar(true);
        });
        setOnlineEventInitialMonth(onlineEventMonth);
    }, [
        eventTimezone,
        isEventOnline,
        month,
        onlineEventInitialMonth,
        selectedDateWithSession,
        shouldShowCalendarSelection,
        showCalendar,
    ]);

    useEffect(() => {
        initialState.onPageMounted();
    }, [initialState]);

    const isOnlineEventInitialMonth = useCallback(() => {
        if (onlineEventInitialMonth && onlineEventInitialMonth.length) {
            const [year, month] = onlineEventInitialMonth[0].date.split('-');
            if (selectedMonth === `${year}_${month}`) {
                return true;
            }
        }
        return false;
    }, [onlineEventInitialMonth, selectedMonth]);

    useEffect(() => {
        if (!shouldShowCalendarSelection || !eventDates[selectedMonth]) {
            return;
        }
        if (isEventOnline && isOnlineEventInitialMonth()) {
            setMonth(onlineEventInitialMonth || []);
        } else {
            setMonth(eventDates[selectedMonth]);
        }
    }, [
        eventDates,
        selectedMonth,
        isEventOnline,
        isOnlineEventInitialMonth,
        onlineEventInitialMonth,
        shouldShowCalendarSelection,
    ]);

    // When re-rendering due to a date change, set the first session of the new date
    useEffect(() => {
        shouldShowCalendarSelection
            ? setSession(
                  selectedDateWithSession?.session ||
                      getFirstAvailableSession(date?.sessions),
              )
            : null;
    }, [date, shouldShowCalendarSelection, selectedDateWithSession]);

    useEffect(() => {
        const checkExperiment = () => {
            try {
                const experimentValue = isInfiniteCalendarExperimentEnabled();

                if (typeof experimentValue === 'boolean') {
                    setIsInfiniteCalendarSeriesExperimentLoading(false);
                    return;
                }

                const timeoutId = setTimeout(() => {
                    setIsInfiniteCalendarSeriesExperimentLoading(false);
                }, INFINITE_CALENDAR_SERIES_EXPERIMENT_LOADING_TIMEOUT);

                return () => clearTimeout(timeoutId);
            } catch (error) {
                setIsInfiniteCalendarSeriesExperimentLoading(false);
            }
        };

        checkExperiment();
    }, []);

    const handleOnSelectDate = (date?: Date) => {
        if (date) {
            const dateIndex = month.findIndex(
                (d) => d.date === formatDate(date),
            );

            onDateChanged(date);
            setDate(month[dateIndex]);
            onSessionSelectedChanged({
                date: month[dateIndex],
                session: month[dateIndex]?.sessions?.[0],
            });
            setIsSelectedSessionSoldOut(false);
        }
    };

    const handleOnMonthChanged = (date: Date) => {
        setSelectedMonth(moment(date).format('YYYY_MM'));
        onMonthChanged(date, timezone);
        setIsSelectedSessionSoldOut(false);
    };

    const soldOut = isSelectedSessionSoldOut || isEventSoldOut;

    const shouldShowSessionsList =
        session &&
        timezone &&
        date?.date &&
        !soldOut &&
        moment(date?.date).format('YYYY_MM') === selectedMonth;

    const sessionsList = shouldShowSessionsList ? (
        <ListPicker
            classNames={classNames('eds-l-pad-all-4', styles.listPicker)}
            itemWrapperClassNames={styles.listPickerItemWrapper}
            itemClassNames={styles.listPickerItem}
            items={getListItemsFromSessions({
                sessions: date?.sessions,
                timezone,
                locale,
                selectedSession: session,
            })}
            onItemPickedChanged={(session) => {
                setSession(session.payload);
                onSessionSelectedChanged({
                    date,
                    session: session.payload,
                });
            }}
        ></ListPicker>
    ) : null;

    const modifiers = {
        soldOut: getSoldOutDates(month || [], timezone),
    };

    const soldOutMessage = isEventSoldOut
        ? SOLD_OUT_EVENT_MESSAGE
        : isSelectedSessionSoldOut
        ? SOLD_OUT_SESSION_MESSAGE
        : '';

    const eventSoldOutMessage = soldOut ? (
        <SeriesNotification
            text={soldOutMessage}
            isAppRebrandActive={isAppRebrandActive}
        ></SeriesNotification>
    ) : undefined;

    const shouldShowSeriesInfiniteCalendarView =
        isInfiniteCalendarExperimentEnabled();

    const seriesPageBody =
        shouldShowCalendarSelection && showCalendar ? (
            <div
                data-testid="timed-entry-body"
                className={styles.seriesPageBody}
            >
                <div className={styles.seriesPageWrapper}>
                    <h2 className="eds-text-bl eds-l-mar-bot-4">
                        {gettext('Available times')}
                    </h2>
                    {eventSoldOutMessage}
                    <DatePicker
                        className={styles.seriesCalendar}
                        defaultMonth={defaultMonth}
                        disabled={isDateDisabled(month)}
                        fromMonth={
                            isEventSoldOut
                                ? utcToZonedTime(
                                      startDate.utc,
                                      startDate.timezone,
                                  )
                                : fromMonth
                        }
                        isAppRebrandActive={isAppRebrandActive}
                        locale={Locales?.[locale.replace(/[_-]/g, '')]}
                        mode="single"
                        modifiers={modifiers}
                        modifiersClassNames={{ soldOut: styles.soldOut }}
                        onMonthChange={handleOnMonthChanged}
                        onSelect={handleOnSelectDate}
                        required={true}
                        toMonth={utcToZonedTime(endDate.utc, calendarTimezone)}
                        selected={
                            isEventSoldOut
                                ? undefined
                                : getLocalDate(
                                      selectedDateWithSession?.date.date ||
                                          date?.date ||
                                          '',
                                  )
                        }
                    ></DatePicker>
                    <Divider />
                    {sessionsList}
                </div>
            </div>
        ) : (
            <>
                <SeriesEventDatePickerContainer data-testid="SeriesEventDatePickerContainer" />

                <div
                    className={styles.seriesEventsContainer}
                    data-automation="series-events-container"
                    data-testid="series-events-container"
                >
                    <SeriesEventCardListContainer data-testid="SeriesEventCardListContainer" />
                </div>
            </>
        );

    const secondaryTitle = shouldShowCalendarSelection
        ? isAppRebrandActive
            ? gettext('Select day and time')
            : eventFromStartDateToEndDate
        : gettext('Multiple Dates');

    const handleSubmit = !shouldShowCalendarSelection
        ? null
        : () => (session?.id ? onContinue(session.id) : null);

    const isSubmitDisabled = !shouldShowCalendarSelection || soldOut;

    const dateReminder = !soldOut ? (
        <DateReminder
            startDatetime={session?.startDatetime}
            locale={locale}
            timezone={timezone}
            isAppRebrandActive={isAppRebrandActive}
        />
    ) : undefined;

    const modalFooterNode =
        shouldShowCalendarSelection && timezone ? (
            <div
                className={classNames(styles.footerNode, 'eds-align--right', {
                    [styles.mainCtaSoldOut]: isAppRebrandActive && soldOut,
                })}
            >
                {dateReminder}
                <Button
                    onClick={handleSubmit}
                    __containerClassName={classNames(
                        'checkout-modal-footer-scrollable-on-desktop',
                        { [styles.mainCta]: isAppRebrandActive },
                    )}
                    style={
                        isAppRebrandActive ? STYLE_REBRANDING_FILL : STYLE_FILL
                    }
                    disabled={isSubmitDisabled}
                    keepDisabledStyleWhileLoading={true}
                    data-spec="eds-modal__primary-button"
                    data-automation="eds-modal__primary-button"
                    data-testid="eds-modal__primary-button"
                    isFullWidth={!isAppRebrandActive}
                >
                    {gettext('Continue')}
                </Button>
            </div>
        ) : undefined;

    const ctaText = shouldShowCalendarSelection
        ? gettext('Continue')
        : undefined;
    const tertiaryNode = isAppRebrandActive ? (
        <></>
    ) : (
        <LanguageAndBrand isSeriesPage={true} />
    );

    if (isInfiniteCalendarSeriesExperimentLoading) {
        return (
            <PageTemplate
                notificationOptions={[]}
                templateProps={{} as TemplateProps}
                isAppRebrandActive={isAppRebrandActive}
                containerClassName="eds-align--center-horizontal eds-l-mar-bot-3"
            >
                <LoadingOverlay
                    size="large-thin"
                    isShown={true}
                    style={getLoadingOverlayStyle(isAppRebrandActive)}
                />
            </PageTemplate>
        );
    }

    if (shouldShowSeriesInfiniteCalendarView) {
        return (
            <ConnectedInfiniteCalendarSeries>
                {/* `children` in this context refers to the container component passed to the child route of the `SERIES_PATH`.
                    It will be rendered inside the tickets section of the InfiniteCalendarSeries component
                */}
                {children}
            </ConnectedInfiniteCalendarSeries>
        );
    }

    return (
        <ConnectedPageTemplate
            containerClassName="eds-align--center-horizontal eds-l-mar-bot-3"
            ctaText={ctaText}
            extraModalBodyProps={extraModalBodyProps}
            extraPaneProps={extraPaneProps}
            handleSubmit={handleSubmit}
            isSubmitDisabled={isSubmitDisabled}
            secondaryTitle={secondaryTitle}
            tertiaryNode={tertiaryNode}
            modalFooterNode={modalFooterNode}
        >
            {seriesPageBody}

            <LoadingOverlay
                size="large-thin"
                isShown={isLoading}
                style={getLoadingOverlayStyle(isAppRebrandActive)}
            />
        </ConnectedPageTemplate>
    );
};
